/**
 * @fileoverview Project Tasks Component
 *
 * Displays and manages project tasks with:
 * - List view with pagination
 * - Calendar view with task indicators
 * - Search and filtering (status, phase, vendor)
 * - Task creation, viewing, and editing via modals
 *
 * Features:
 * - Dual view modes (list/calendar)
 * - Advanced filtering and search
 * - Inline task editing
 * - Modal-based task management
 *
 * @version 1.0.0
 */

import React, { useState, useMemo } from "react";
import {
  FaTasks,
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronUp,
  FaChevronDown,
  FaCalendarAlt,
  FaList,
  FaPencilAlt,
  FaCheckCircle,
  FaHourglassHalf,
  FaClock,
  FaExclamationTriangle,
  FaNewspaper,
  FaPaperclip,
  FaClipboardList,
  FaAngleDoubleLeft,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleRight,
} from "react-icons/fa";
import { formatDateET } from "../../../../utils/dateUtils";
import TaskModal from "../../../../components/admin/TaskModal";
import TaskViewerModal from "../../../../components/admin/TaskViewerModal";
import MiniCalendar from "../../../../components/admin/MiniCalendar";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import "./ProjectTasks.css";

/* ============================================
   CONSTANTS
   ============================================ */

/**
 * Construction phase options
 * @constant {Array<Object>}
 */
const CONSTRUCTION_PHASES = [
  { value: "Planning/Permitting", label: "Planning/Permitting", progress: 5 },
  { value: "Foundation", label: "Foundation", progress: 15 },
  { value: "Framing", label: "Framing", progress: 30 },
  { value: "Rough-In", label: "Rough-In", progress: 45 },
  {
    value: "Insulation & Drywall",
    label: "Insulation & Drywall",
    progress: 55,
  },
  { value: "Trim & Paint", label: "Trim & Paint", progress: 70 },
  { value: "Mechanical Trim", label: "Mechanical Trim", progress: 80 },
  { value: "Final Finishes", label: "Final Finishes", progress: 90 },
  { value: "Punch List", label: "Punch List", progress: 95 },
  {
    value: "Final Inspection / CO",
    label: "Final Inspection / CO",
    progress: 98,
  },
  { value: "Complete", label: "Complete", progress: 100 },
];

/**
 * Task status filter options
 * @constant {Array<Object>}
 */
const TASK_STATUSES = [
  { value: "all", label: "All Tasks" },
  { value: "Not started", label: "Not Started" },
  { value: "In progress", label: "In Progress" },
  { value: "Delayed", label: "Delayed" },
  { value: "Completed", label: "Completed" },
];

/**
 * Tasks per page for list view
 * @constant {number}
 */
const TASKS_PER_PAGE = 4;

/* ============================================
   HELPER FUNCTIONS
   ============================================ */

/**
 * Get task status icon based on status
 * @param {string} status - Task status
 * @returns {JSX.Element} Icon component
 */
const getTaskStatusIcon = (status) => {
  switch (status) {
    case "Completed":
      return <FaCheckCircle className="task-icon-completed" />;
    case "In progress":
      return <FaHourglassHalf className="task-icon-progress" />;
    case "Not started":
      return <FaClock className="task-icon-notstarted" />;
    case "Delayed":
      return <FaExclamationTriangle className="task-icon-delayed" />;
    default:
      return <FaClock />;
  }
};

/**
 * Get tasks for a selected date
 * @param {Array} tasks - All tasks
 * @param {Date} selectedDate - Selected date
 * @returns {Array} Tasks for the date
 */
const getTasksForSelectedDate = (tasks, selectedDate) => {
  const dateString = selectedDate.toISOString().split("T")[0];
  return tasks.filter((task) => {
    const taskStartDate = task.startDate?.split("T")[0];
    const taskEndDate = task.endDate?.split("T")[0];
    const taskCompletedDate = task.dateCompleted?.split("T")[0];

    return (
      taskStartDate === dateString ||
      taskEndDate === dateString ||
      taskCompletedDate === dateString
    );
  });
};

/**
 * Get task counts by date for calendar indicators
 * @param {Array} tasks - All tasks
 * @returns {Object} Object with dates as keys and counts as values
 */
const getTasksByDate = (tasks) => {
  const tasksByDate = {};

  tasks.forEach((task) => {
    if (task.startDate) {
      const startDate = task.startDate.split("T")[0];
      tasksByDate[startDate] = (tasksByDate[startDate] || 0) + 1;
    }

    if (task.endDate) {
      const endDate = task.endDate.split("T")[0];
      tasksByDate[endDate] = (tasksByDate[endDate] || 0) + 1;
    }

    if (task.dateCompleted) {
      const completedDate = task.dateCompleted.split("T")[0];
      tasksByDate[completedDate] = (tasksByDate[completedDate] || 0) + 1;
    }
  });

  return tasksByDate;
};

/* ============================================
   SUB-COMPONENTS
   ============================================ */

/**
 * Task Row Component
 * Displays individual task with status, dates, and edit button
 */
const TaskRow = ({ task, onClick, onEdit }) => {
  const getStatusClass = () => {
    switch (task.status) {
      case "Completed":
        return "apd-task-status-completed";
      case "In progress":
        return "apd-task-status-in-progress";
      case "Delayed":
        return "apd-task-status-delayed";
      case "Not started":
      default:
        return "apd-task-status-not-started";
    }
  };

  const getDateDisplay = () => {
    if (task.status === "Completed" && task.dateCompleted) {
      return (
        <>
          <span>{formatDateET(task.startDate)}</span>
          <span className="apd-task-date-sep">→</span>
          <span>{formatDateET(task.dateCompleted)}</span>
        </>
      );
    }
    if (task.startDate && task.endDate) {
      return (
        <>
          <span>{formatDateET(task.startDate)}</span>
          <span className="apd-task-date-sep">→</span>
          <span>{formatDateET(task.endDate)}</span>
        </>
      );
    }
    if (task.startDate) {
      return <span>Start: {formatDateET(task.startDate)}</span>;
    }
    return <span>No date</span>;
  };

  const vendorName = task.vendorName;
  const phaseName = task.constructionPhase;

  return (
    <div className="apd-task-row">
      {/* Status Icon */}
      <div className="apd-task-status-icon">
        {getTaskStatusIcon(task.status)}
      </div>

      {/* Main Task Info - Clickable */}
      <div className="apd-task-main" onClick={() => onClick(task)}>
        <div className="apd-task-info">
          {/* Task Name Row with Digest Badge */}
          <div className="apd-task-name-row">
            <span className="apd-task-name">{task.taskName}</span>
            {(task.attachments?.length > 0 ||
              task.filesAndPhotos?.length > 0) && (
              <span
                className="apd-task-digest-badge"
                data-tooltip-br="Has attachments"
              >
                <FaPaperclip />
              </span>
            )}
            {task.includeInDigest && (
              <span
                className="apd-task-digest-badge"
                data-tooltip-br="Included in Weekly Digest"
              >
                <FaNewspaper />
              </span>
            )}
          </div>

          {/* Meta Info (Vendor & Phase) */}
          <span className="apd-task-meta">
            {vendorName && phaseName
              ? `${vendorName} • ${phaseName}`
              : vendorName
                ? vendorName
                : phaseName
                  ? phaseName
                  : "No vendor or phase"}
          </span>

          {/* Completed By Info */}
          {task.status === "Completed" && task.completedBy && (
            <span className="apd-task-completed-by">
              Completed by {task.completedBy}
            </span>
          )}
        </div>

        {/* Dates */}
        <div className="apd-task-dates">{getDateDisplay()}</div>

        {/* Status Badge */}
        <span className={`apd-task-status ${getStatusClass()}`}>
          {task.status}
        </span>
      </div>

      {/* Edit Button */}
      <button
        className="apd-task-edit-btn"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(task);
        }}
        data-tooltip-bottom="Edit task"
      >
        <FaPencilAlt />
      </button>
    </div>
  );
};

/**
 * Task Controls Row Component
 * Search input, filter toggle, and view mode toggle
 */
const TaskControlsRow = ({
  searchTerm,
  onSearchChange,
  filtersOpen,
  onToggleFilters,
  viewMode,
  onViewModeChange,
  activeFilterCount,
  disableSearch,
}) => (
  <div className="apd-task-controls">
    {/* Search Input */}
    <div className={`apd-task-search ${disableSearch ? "disabled" : ""}`}>
      <FaSearch className="apd-task-search-icon" />
      <input
        type="text"
        placeholder="Search tasks..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="apd-task-search-input"
        disabled={disableSearch}
      />
    </div>

    {/* Control Buttons */}
    <div className="apd-task-control-buttons">
      {/* Filter Toggle */}
      <button
        className={`apd-task-filter-btn ${filtersOpen ? "active" : ""}`}
        onClick={onToggleFilters}
      >
        <FaFilter />
        <span>Filters</span>
        {activeFilterCount > 0 && (
          <span className="apd-task-filter-count">{activeFilterCount}</span>
        )}
        {filtersOpen ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      {/* View Mode Toggle */}
      <div className="apd-photo-view-toggle">
        <button
          className={`apd-photo-view-btn ${
            viewMode === "calendar" ? "active" : ""
          }`}
          onClick={() => onViewModeChange("calendar")}
        >
          <FaCalendarAlt />
          <span className="hide-sm">Calendar</span>
        </button>
        <button
          className={`apd-photo-view-btn ${viewMode === "list" ? "active" : ""}`}
          onClick={() => onViewModeChange("list")}
        >
          <FaList />
          <span className="hide-sm">List</span>
        </button>
      </div>
    </div>
  </div>
);

/**
 * Task Filter Panel Component
 * Collapsible filter panel with status, phase, and vendor filters
 */
const TaskFilterPanel = ({
  filters,
  onFilterChange,
  onClearFilters,
  isVisible,
  vendorOptions,
  phaseOptions,
}) => {
  if (!isVisible) return null;

  return (
    <div className="apd-task-filters">
      <div className="apd-task-filter-row">
        {/* Status Filter */}
        <div className="apd-task-filter-group">
          <label className="apd-task-filter-label">Status</label>
          <select
            value={filters.status}
            onChange={(e) => onFilterChange("status", e.target.value)}
            className="apd-task-filter-select"
          >
            {TASK_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        {/* Phase Filter */}
        <div className="apd-task-filter-group">
          <label className="apd-task-filter-label">Phase</label>
          <select
            value={filters.phase}
            onChange={(e) => onFilterChange("phase", e.target.value)}
            className="apd-task-filter-select"
          >
            {phaseOptions.map((phase) => (
              <option key={phase.value} value={phase.value}>
                {phase.label}
              </option>
            ))}
          </select>
        </div>

        {/* Vendor Filter */}
        <div className="apd-task-filter-group">
          <label className="apd-task-filter-label">Assigned Vendor</label>
          <select
            value={filters.vendor}
            onChange={(e) => onFilterChange("vendor", e.target.value)}
            className="apd-task-filter-select"
          >
            {vendorOptions.map((vendor) => (
              <option key={vendor.value} value={vendor.value}>
                {vendor.label}
              </option>
            ))}
          </select>
        </div>

        {/* Weekly Digest Filter */}
        <div className="apd-task-filter-group apd-task-filter-checkbox">
          <label className="apd-task-filter-checkbox-label">
            <input
              type="checkbox"
              checked={filters.digest}
              onChange={(e) => onFilterChange("digest", e.target.checked)}
            />
            <span>
              In Weekly Digest <FaNewspaper />
            </span>
          </label>
        </div>

        {/* Clear Filters Button */}
        <button className="apd-task-clear-filters" onClick={onClearFilters}>
          Clear All
        </button>
      </div>
    </div>
  );
};

/**
 * Pagination Component
 * Page navigation for list view
 */
const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="apd-pagination">
    <button
      className="apd-pagination-btn"
      onClick={() => onPageChange(1)}
      disabled={currentPage === 1}
      data-tooltip="First page"
    >
      <FaAngleDoubleLeft />
    </button>
    <button
      className="apd-pagination-btn"
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 1}
      data-tooltip="Previous page"
    >
      <FaAngleLeft />
    </button>
    <span className="apd-pagination-info">
      Page {currentPage} of {totalPages}
    </span>
    <button
      className="apd-pagination-btn"
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      data-tooltip="Next Page"
    >
      <FaAngleRight />
    </button>
    <button
      className="apd-pagination-btn"
      onClick={() => onPageChange(totalPages)}
      disabled={currentPage === totalPages}
      data-tooltip="Last page"
    >
      <FaAngleDoubleRight />
    </button>
  </div>
);

/**
 * Section wrapper component
 */
const Section = ({ title, icon, children, action }) => (
  <div className="apd-section">
    <div className="apd-section-header">
      <div className="apd-section-title">
        {icon && <span className="apd-section-icon">{icon}</span>}
        <h2>{title}</h2>
      </div>
      {action && <div className="apd-section-action">{action}</div>}
    </div>
    <div className="apd-section-content">{children}</div>
  </div>
);

/* ============================================
   MAIN COMPONENT
   ============================================ */

/**
 * Parse a compound "Task Order" value (e.g. "4-10", "8-") into a
 * [major, minor] numeric tuple for sorting. Missing or non-numeric
 * parts fall back to Infinity (major) / 0 (minor) so they sort last.
 *
 * @param {*} value - Raw Task Order value from Airtable
 * @return {[number, number]} [major, minor]
 */
const parseTaskOrder = (value) => {
  if (value == null || value === "") return [Infinity, 0];
  const [major, minor] = String(value).split("-");
  const majorNum = Number(major);
  const minorNum = Number(minor);
  return [
    Number.isFinite(majorNum) ? majorNum : Infinity,
    Number.isFinite(minorNum) ? minorNum : 0,
  ];
};

/**
 * ProjectTasks Component
 *
 * @param {Object} props - Component props
 * @param {string} props.projectId - Project ID
 * @param {Array} props.tasks - Array of tasks (React Query response)
 * @param {boolean} props.createTaskList - Whether critical path task list has been created
 * @param {Function} props.onUpdateProject - Update handler for project fields
 * @param {boolean} props.isUpdating - Whether update is in progress
 */
const ProjectTasks = ({
  projectId,
  projectName,
  tasks: tasksData = [],
  createTaskList = false,
  onUpdateProject,
  isUpdating = false,
}) => {
  /* ============================================
     DATA EXTRACTION
     ============================================ */

  /**
   * Extract tasks array from React Query response
   * Handles both nested data.data and direct data structures
   */
  const tasks = useMemo(() => {
    return tasksData?.data || tasksData || [];
  }, [tasksData]);

  /* ============================================
     STATE MANAGEMENT
     ============================================ */

  // Search and filters
  const [taskSearchTerm, setTaskSearchTerm] = useState("");
  const [taskFiltersOpen, setTaskFiltersOpen] = useState(false);
  const [taskFilters, setTaskFilters] = useState({
    status: "all",
    phase: "all",
    vendor: "all",
    digest: false,
  });

  // View mode and pagination
  const [taskPage, setTaskPage] = useState(1);
  const [taskViewMode, setTaskViewMode] = useState("list"); // "list" or "calendar"
  const [selectedTaskDate, setSelectedTaskDate] = useState(new Date());

  // Modals
  const [editTaskModal, setEditTaskModal] = useState({
    isOpen: false,
    taskId: null,
  });
  const [createTaskModal, setCreateTaskModal] = useState({ isOpen: false });
  const [taskViewModal, setTaskViewModal] = useState({
    isOpen: false,
    task: null,
  });
  const [showCreateTaskListModal, setShowCreateTaskListModal] = useState(false);
  const [isCreatingTaskList, setIsCreatingTaskList] = useState(false);

  /* ============================================
     COMPUTED VALUES
     ============================================ */

  // Task phase filter options
  const TASK_PHASE_FILTERS = useMemo(() => {
    return [{ value: "all", label: "All Phases" }, ...CONSTRUCTION_PHASES];
  }, []);

  // Get unique vendors from tasks for filter dropdown
  const taskVendorOptions = useMemo(() => {
    if (!Array.isArray(tasks) || tasks.length === 0) {
      return [{ value: "all", label: "All Vendors" }];
    }

    const uniqueVendors = [
      ...new Set(tasks.map((task) => task.vendorName).filter(Boolean)),
    ].sort();

    return [
      { value: "all", label: "All Vendors" },
      ...uniqueVendors.map((vendor) => ({ value: vendor, label: vendor })),
    ];
  }, [tasks]);

  // Count active filters
  const activeTaskFilterCount = useMemo(() => {
    return Object.values(taskFilters).filter((v) => v !== "all" && v !== false)
      .length;
  }, [taskFilters]);

  /* ============================================
     TASK FILTERING & SORTING
     ============================================ */

  const filteredAndSortedTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];

    return tasks
      .filter((task) => {
        // Search filter
        if (taskSearchTerm) {
          const searchLower = taskSearchTerm.toLowerCase();
          const matchesSearch =
            task.taskName?.toLowerCase().includes(searchLower) ||
            task.taskDescription?.toLowerCase().includes(searchLower) ||
            task.taskNotes?.toLowerCase().includes(searchLower) ||
            task.vendorName?.toLowerCase().includes(searchLower) ||
            task.constructionPhase?.toLowerCase().includes(searchLower);

          if (!matchesSearch) return false;
        }

        // Status filter
        if (
          taskFilters.status !== "all" &&
          task.status !== taskFilters.status
        ) {
          return false;
        }

        // Phase filter
        if (
          taskFilters.phase !== "all" &&
          task.constructionPhase !== taskFilters.phase
        ) {
          return false;
        }

        // Vendor filter
        if (
          taskFilters.vendor !== "all" &&
          task.vendorName !== taskFilters.vendor
        ) {
          return false;
        }

        // Weekly digest filter
        if (taskFilters.digest && !task.includeInDigest) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        // Unless the user is filtering by status, completed tasks sink to
        // the end of the list, sorted by end date (most recent first).
        if (taskFilters.status === "all") {
          const aDone = a.status === "Completed";
          const bDone = b.status === "Completed";

          if (aDone && !bDone) return 1;
          if (!aDone && bDone) return -1;

          if (aDone && bDone) {
            const aEnd = a.endDate ? new Date(a.endDate).getTime() : 0;
            const bEnd = b.endDate ? new Date(b.endDate).getTime() : 0;
            return bEnd - aEnd;
          }
        }

        // Non-completed tasks with an actual start date come first,
        // sorted by start date asc (earliest first).
        if (a.startDate && b.startDate) {
          return new Date(a.startDate) - new Date(b.startDate);
        }
        if (a.startDate) return -1;
        if (b.startDate) return 1;

        // Remaining (generated critical-path) tasks: by Task Order asc.
        // Task Order is a compound "major-minor" key (e.g. "4-10", "8-"),
        // so parse each part numerically rather than coercing the whole string.
        const [aMajor, aMinor] = parseTaskOrder(a.taskOrder);
        const [bMajor, bMinor] = parseTaskOrder(b.taskOrder);
        return aMajor - bMajor || aMinor - bMinor;
      });
  }, [tasks, taskSearchTerm, taskFilters]);

  // Get tasks for selected date (for calendar view)
  const tasksForSelectedDate = useMemo(() => {
    return getTasksForSelectedDate(filteredAndSortedTasks, selectedTaskDate);
  }, [filteredAndSortedTasks, selectedTaskDate]);

  // Get task counts by date (for calendar indicators)
  const tasksByDate = useMemo(() => {
    return getTasksByDate(filteredAndSortedTasks);
  }, [filteredAndSortedTasks]);

  // Pagination logic (for list view only)
  const paginatedTasks = useMemo(() => {
    const startIndex = (taskPage - 1) * TASKS_PER_PAGE;
    return filteredAndSortedTasks.slice(
      startIndex,
      startIndex + TASKS_PER_PAGE,
    );
  }, [filteredAndSortedTasks, taskPage]);

  const totalTaskPages = Math.ceil(
    filteredAndSortedTasks.length / TASKS_PER_PAGE,
  );

  /* ============================================
     HANDLERS
     ============================================ */

  /**
   * Handle task filter change
   */
  const handleTaskFilterChange = (filterType, value) => {
    setTaskFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setTaskPage(1); // Reset to first page
  };

  /**
   * Clear all task filters
   */
  const handleClearTaskFilters = () => {
    setTaskFilters({
      status: "all",
      phase: "all",
      vendor: "all",
      digest: false,
    });
    setTaskPage(1);
  };

  /**
   * Open edit task modal
   */
  const openEditTaskModal = (task) => {
    setEditTaskModal({ isOpen: true, taskId: task.id });
  };

  /**
   * Close edit task modal
   */
  const closeEditTaskModal = () => {
    setEditTaskModal({ isOpen: false, taskId: null });
  };

  /**
   * Open create task modal
   */
  const openCreateTaskModal = () => {
    setCreateTaskModal({ isOpen: true });
  };

  /**
   * Close create task modal
   */
  const closeCreateTaskModal = () => {
    setCreateTaskModal({ isOpen: false });
  };

  /**
   * Open task viewer modal
   */
  const openTaskViewModal = (task) => {
    setTaskViewModal({ isOpen: true, task });
  };

  /**
   * Close task viewer modal
   */
  const closeTaskViewModal = () => {
    setTaskViewModal({ isOpen: false, task: null });
  };

  /**
   * Handle edit from viewer
   * Opens edit modal and closes viewer modal
   */
  const handleEditFromViewer = () => {
    if (taskViewModal.task) {
      openEditTaskModal(taskViewModal.task);
      closeTaskViewModal();
    }
  };

  /**
   * Handle create task list confirmation
   * Updates project to set createTaskList flag
   */
  const handleCreateTaskList = async () => {
    console.log("[ProjectTasks] Creating task list...");
    setIsCreatingTaskList(true);

    try {
      if (!onUpdateProject) {
        console.error("[ProjectTasks] onUpdateProject is not provided");
        alert(
          "Error: Update function not available. Please refresh and try again.",
        );
        setIsCreatingTaskList(false);
        return;
      }

      console.log(
        "[ProjectTasks] Calling onUpdateProject with createTaskList: true",
      );
      const result = await onUpdateProject({ createTaskList: true });
      console.log("[ProjectTasks] Task list created successfully", result);

      // Close modal only after successful update
      setShowCreateTaskListModal(false);
      setIsCreatingTaskList(false);
    } catch (error) {
      console.error("[ProjectTasks] Error creating task list:", error);
      setIsCreatingTaskList(false);

      // Show error to user
      const errorMessage = error?.message || "Failed to create task list";
      alert(
        `Error: ${errorMessage}\n\nPlease try again or contact support if the issue persists.`,
      );

      // Keep modal open so user can try again
    }
  };

  /* ============================================
     RENDER
     ============================================ */

  return (
    <Section
      title="Project Tasks"
      icon={<FaTasks />}
      action={
        <button className="apd-add-btn" onClick={openCreateTaskModal}>
          <FaPlus /> <span className="hide-sm">Add Task</span>
        </button>
      }
    >
      {/* Controls Row: Search + Filters + View Toggle */}
      <TaskControlsRow
        searchTerm={taskSearchTerm}
        onSearchChange={setTaskSearchTerm}
        filtersOpen={taskFiltersOpen}
        onToggleFilters={() => setTaskFiltersOpen(!taskFiltersOpen)}
        viewMode={taskViewMode}
        onViewModeChange={setTaskViewMode}
        activeFilterCount={activeTaskFilterCount}
        disableSearch={taskViewMode === "calendar"}
      />
      {/* Filter Panel (Accordion) */}
      <TaskFilterPanel
        filters={taskFilters}
        onFilterChange={handleTaskFilterChange}
        onClearFilters={handleClearTaskFilters}
        isVisible={taskFiltersOpen}
        vendorOptions={taskVendorOptions}
        phaseOptions={TASK_PHASE_FILTERS}
      />
      {/* Calendar View */}
      {taskViewMode === "calendar" && (
        <div className="apd-tasks-calendar-view">
          {/* Left: Task List for Selected Date */}
          <div className="apd-tasks-calendar-list">
            <div className="apd-tasks-calendar-list-header">
              <span className="apd-tasks-date-label">
                {selectedTaskDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              <span className="apd-tasks-count">
                {tasksForSelectedDate.length} task
                {tasksForSelectedDate.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="apd-tasks-calendar-list-items">
              {tasksForSelectedDate.length > 0 ? (
                tasksForSelectedDate.map((task) => (
                  <span photo-tooltip="View task details">
                    <TaskRow
                      key={task.id}
                      task={task}
                      onClick={openTaskViewModal}
                      onEdit={openEditTaskModal}
                    />
                  </span>
                ))
              ) : (
                <p className="apd-empty-message">No tasks for this date</p>
              )}
            </div>
          </div>

          {/* Right: Calendar */}
          <div className="apd-tasks-calendar-wrapper">
            <MiniCalendar
              selectedDate={selectedTaskDate}
              onDateSelect={setSelectedTaskDate}
              tasksForDate={tasksByDate}
            />
          </div>
        </div>
      )}
      {/* List View */}
      {taskViewMode === "list" && (
        <>
          {/* Create Task List Option - Shows only when no tasks exist and not already created */}
          {tasks.length === 0 && !createTaskList && (
            <div className="pt-create-task-list-prompt">
              <div className="pt-create-content">
                <h3 className="pt-create-title">
                  <FaClipboardList className="pt-create-icon" />
                  No Tasks Yet
                </h3>
                <button
                  className="pt-create-btn"
                  onClick={() => setShowCreateTaskListModal(true)}
                  disabled={isCreatingTaskList || isUpdating}
                >
                  {isCreatingTaskList
                    ? "Creating..."
                    : "Create Critical Path Task List"}
                </button>
                <p className="pt-create-note">
                  This will add standard construction tasks for this project
                  type. This action cannot be undone.
                </p>
              </div>
            </div>
          )}

          {/* Task List */}
          <div className="apd-tasks-list">
            {paginatedTasks.length > 0 ? (
              paginatedTasks.map((task) => (
                <span key={task.id} photo-tooltip="View task details">
                  <TaskRow
                    task={task}
                    onClick={openTaskViewModal}
                    onEdit={openEditTaskModal}
                  />
                </span>
              ))
            ) : tasks.length > 0 ? (
              <p className="apd-empty-message">
                {taskSearchTerm || activeTaskFilterCount > 0
                  ? "No tasks match your search or filters"
                  : "No tasks found"}
              </p>
            ) : null}
          </div>
          {totalTaskPages > 1 && (
            <Pagination
              currentPage={taskPage}
              totalPages={totalTaskPages}
              onPageChange={setTaskPage}
            />
          )}
        </>
      )}
      {/* Task Viewer Modal */}
      <TaskViewerModal
        isOpen={taskViewModal.isOpen}
        onClose={closeTaskViewModal}
        task={taskViewModal.task}
        onEdit={handleEditFromViewer}
      />
      {/* Edit Task Modal */}
      <TaskModal
        isOpen={editTaskModal.isOpen}
        onClose={closeEditTaskModal}
        taskId={editTaskModal.taskId}
        onSave={() => {
          // Task updated - data auto-refreshes via React Query
        }}
      />
      {/* Create Task Modal */}
      <TaskModal
        isOpen={createTaskModal.isOpen}
        onClose={closeCreateTaskModal}
        taskId={null}
        projectId={projectId}
        projectName={projectName}
        onSave={() => {
          // Task created - data auto-refreshes via React Query
        }}
      />
      {/* Create Task List Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCreateTaskListModal}
        onClose={() => !isCreatingTaskList && setShowCreateTaskListModal(false)}
        onConfirm={handleCreateTaskList}
        title="Create Critical Path Task List?"
        message="This will automatically populate this project with standard construction tasks."
        warning="This action cannot be undone."
        confirmText="Create Task List"
        cancelText="Cancel"
        isDestructive={false}
        isLoading={isCreatingTaskList}
      />
    </Section>
  );
};

export default ProjectTasks;
